import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { IMAGES } from "@/data/brand";
import { useI18n } from "@/i18n";

const reveal = {
  hidden: { opacity: 0, y: 60 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.9, ease: [0.22, 1, 0.36, 1] },
  },
};

export const Manifesto = () => {
  const { t } = useI18n();
  const m = t.manifesto;
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], ["-12%", "12%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1.25, 1.05]);

  return (
    <section
      id="manifesto"
      ref={ref}
      data-testid="manifesto-section"
      className="relative mx-auto max-w-[1400px] px-6 lg:px-10 py-32 lg:py-44"
    >
      <div className="grid lg:grid-cols-12 gap-16 items-start">
        <motion.div
          variants={reveal}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.3 }}
          className="lg:col-span-5 lg:sticky lg:top-32"
        >
          <div className="relative overflow-hidden rounded-2xl border border-white/[0.08] aspect-[4/5]">
            <motion.img
              src={IMAGES.justice}
              alt={m.captionTitle}
              style={{ y: imgY, scale: imgScale }}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0a0f1a] via-[#0a0f1a]/20 to-transparent" />
            <div className="absolute bottom-6 left-6 right-6">
              <p className="text-xs uppercase tracking-[0.3em] text-blue-300/80">
                {m.caption}
              </p>
              <p className="mt-2 font-display text-xl text-white">
                {m.captionTitle}
              </p>
            </div>
          </div>
        </motion.div>

        <div className="lg:col-span-7 lg:pl-8">
          <motion.h2
            variants={reveal}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.5 }}
            className="font-display text-3xl sm:text-4xl font-light tracking-tight text-white max-w-lg"
          >
            {m.heading}
          </motion.h2>

          <div className="mt-16 space-y-16">
            {m.chapters.map((c) => (
              <motion.div
                key={c.num}
                variants={reveal}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.4 }}
                data-testid={`manifesto-chapter-${c.num}`}
                className="flex gap-8 border-t border-white/[0.08] pt-8"
              >
                <span className="font-display text-5xl lg:text-6xl font-light text-white/10 shrink-0 leading-none">
                  {c.num}
                </span>
                <div>
                  <h3 className="font-display text-2xl font-medium text-white mb-3">
                    {c.title}
                  </h3>
                  <p className="text-white/55 leading-relaxed max-w-md">
                    {c.body}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
